import { useState, useEffect } from "react";
import axios from "axios";
import { getApiUrl } from "@/utils/api";
import { Label } from "@/components/ui/label";
import { ColorPickerInput } from "@/components/ui/ColorPickerInput";
import { Loader2, Palette, Type, ImageIcon } from "lucide-react";
import ModuleInstancesPreview from "./ModuleInstancesPreview";
import { useSiteStyles } from "@/hooks/useSiteStyles";
import useAutoSave from "@/hooks/useAutoSave";
import { AutoSaveStatus } from "@/components/ui/AutoSaveStatus";
import { GOOGLE_FONTS } from "@/utils/fonts";

const API = getApiUrl();

const DEFAULT_STYLES = {
  primaryHex: "#2f4f4f",
  secondaryHex: "#4a7a7a",
  accentHex: "#e07b5d",
  backgroundHex: "#f5f2ed",
  sectionBgHex: "#ffffff",
  cardBgHex: "#ffffff",
  textHex: "#1a1a1a",
  mutedTextHex: "#666666",
  borderHex: "#e5e0d8",
  headingHex: "#1a1a1a",
  headingAccentHex: "#e07b5d",
  linkHex: "#2f4f4f",
  linkHoverHex: "#1a3030",
  navBgHex: "#ffffff",
  navTextHex: "#1a1a1a",
  navHoverHex: "#2f4f4f",
  footerBgHex: "#2f4f4f",
  footerTextHex: "#ffffff",
  footerLinkHex: "#ffffff",
  buttonBgHex: "#2f4f4f",
  buttonTextHex: "#ffffff",
  headingFont: "Playfair Display",
  bodyFont: "Inter",
  navFont: "",
  listFont: "",
  baseFontSize: 16,
  listFontSize: 16,
  headingFontSize: 32,
  h1Size: 48,
  h2Size: 36,
  h3Size: 24,
  h4Size: 20,
  h5Size: 18,
  h6Size: 16,
  headingWeight: 700,
  bodyWeight: 400,
  bodyLineHeight: 1.6,
  headingLineHeight: 1.2,
  headingSpacing: 16,
  containerMaxWidth: 1280,
  imageBorderRadius: 0,
  imageShadowIntensity: 0,
};

const SHADOW_LABELS = ["None", "Soft", "Medium", "Strong"];
const SHADOW_PREVIEW = [
  "none",
  "0 2px 8px rgba(0,0,0,0.08)",
  "0 4px 16px rgba(0,0,0,0.15)",
  "0 8px 30px rgba(0,0,0,0.25)",
];

function ColorField({ label, field, value, onChange }) {
  return (
    <ColorPickerInput
      label={label}
      value={value}
      onChange={(v) => onChange(field, v)}
      testId={`color-${field}`}
      allowCustom
    />
  );
}

function FontField({ label, field, value, onChange }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <select
        value={value || ""}
        onChange={(e) => onChange(field, e.target.value)}
        className="h-9 px-2 border border-input rounded bg-background text-sm"
        style={{ fontFamily: value || "inherit" }}
      >
        {GOOGLE_FONTS.map((f, i) =>
          f.group ? (
            <optgroup key={i} label={f.group} />
          ) : (
            <option key={f.value} value={f.value} style={{ fontFamily: f.value || "inherit" }}>
              {f.label}
            </option>
          )
        )}
      </select>
      {value && (
        <p className="text-xs text-muted-foreground" style={{ fontFamily: value }}>
          Preview: The quick brown fox jumps over the lazy dog.
        </p>
      )}
    </div>
  );
}

export function SiteStylesAdminTab() {
  const [styles, setStyles] = useState(DEFAULT_STYLES);
  const [loading, setLoading] = useState(true);
  const { updateStyles } = useSiteStyles();

  useEffect(() => {
    fetchStyles();
  }, []);

  // Load Google Fonts dynamically
  useEffect(() => {
    const fonts = [styles.headingFont, styles.bodyFont, styles.navFont, styles.listFont].filter(Boolean);
    if (fonts.length === 0) return;
    const familiesParam = fonts.map(f => f.replace(/ /g, "+")+":wght@400;500;600;700").join("&family=");
    const linkId = "site-styles-fonts";
    let link = document.getElementById(linkId);
    if (!link) {
      link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    link.href = `https://fonts.googleapis.com/css2?family=${familiesParam}&display=swap`;
  }, [styles.headingFont, styles.bodyFont, styles.navFont, styles.listFont]);

  const fetchStyles = async () => {
    try {
      const res = await axios.get(`${API}/site-styles`);
      setStyles({ ...DEFAULT_STYLES, ...res.data });
    } catch {
      setStyles(DEFAULT_STYLES);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setStyles((prev) => {
      const next = { ...prev, [field]: value };
      // Apply to the DOM immediately so the preview wall (and any other
      // [data-public-site] surface) reflects the change in real time,
      // before the debounced autosave persists to the backend.
      try { updateStyles(next); } catch { /* SiteStylesProvider may not be mounted in some surfaces */ }
      return next;
    });
  };

  // Debounced autosave — persists any change 800 ms after the admin stops
  // editing, so colour pickers and font selects feel "live". Idles until
  // the first change (skipFirst inside the hook guards the initial mount
  // from re-POSTing the freshly-fetched styles).
  const { status, lastSavedAt } = useAutoSave(
    styles,
    async (snapshot) => {
      await axios.put(
        `${API}/admin/site-styles`,
        { styles: snapshot },
        { withCredentials: true },
      );
      // Push into the provider so every mounted public surface picks up
      // the persisted values immediately.
      try { updateStyles(snapshot); } catch { /* noop */ }
      // Broadcast so live header / footer components can invalidate their
      // cached `/api/site-styles` fetches and re-render without a refresh.
      try {
        window.dispatchEvent(new CustomEvent("slh:site-styles-updated", { detail: { styles: snapshot } }));
      } catch { /* noop */ }
    },
    { delay: 800, enabled: !loading, canSave: (v) => !!v },
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Site Styles</h2>
          <p className="text-sm text-muted-foreground mt-1">Global colours and typography applied across all pages</p>
        </div>
        <AutoSaveStatus status={status} lastSavedAt={lastSavedAt} />
      </div>

      {/* Color Palette */}
      <section className="border rounded-lg p-6 space-y-5">
        <div className="flex items-center gap-2 border-b pb-3">
          <Palette className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Colour Palette</h3>
        </div>

        <div className="space-y-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Brand Colours</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <ColorField label="Primary" field="primaryHex" value={styles.primaryHex} onChange={handleChange} />
            <ColorField label="Secondary" field="secondaryHex" value={styles.secondaryHex} onChange={handleChange} />
            <ColorField label="Accent" field="accentHex" value={styles.accentHex} onChange={handleChange} />
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Page Colours</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <ColorField label="Site Background" field="backgroundHex" value={styles.backgroundHex} onChange={handleChange} />
            <ColorField label="Section Background" field="sectionBgHex" value={styles.sectionBgHex} onChange={handleChange} />
            <ColorField label="Card Background" field="cardBgHex" value={styles.cardBgHex} onChange={handleChange} />
            <ColorField label="Body Text" field="textHex" value={styles.textHex} onChange={handleChange} />
            <ColorField label="Muted Text" field="mutedTextHex" value={styles.mutedTextHex} onChange={handleChange} />
            <ColorField label="Headings" field="headingHex" value={styles.headingHex} onChange={handleChange} />
            <ColorField label="Heading Accent (2nd colour)" field="headingAccentHex" value={styles.headingAccentHex} onChange={handleChange} />
            <ColorField label="Links" field="linkHex" value={styles.linkHex} onChange={handleChange} />
            <ColorField label="Link Hover" field="linkHoverHex" value={styles.linkHoverHex} onChange={handleChange} />
            <ColorField label="Borders" field="borderHex" value={styles.borderHex} onChange={handleChange} />
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Header</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <ColorField label="Header Background" field="headerBgHex" value={styles.headerBgHex} onChange={handleChange} />
            <ColorField label="Header Text" field="headerTextHex" value={styles.headerTextHex} onChange={handleChange} />
            <ColorField label="Header Link" field="headerLinkHex" value={styles.headerLinkHex} onChange={handleChange} />
            <ColorField label="Header Link Hover" field="headerLinkHoverHex" value={styles.headerLinkHoverHex} onChange={handleChange} />
            <ColorField label="Header Border" field="headerBorderHex" value={styles.headerBorderHex} onChange={handleChange} />
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Navigation</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <ColorField label="Nav Background" field="navBgHex" value={styles.navBgHex} onChange={handleChange} />
            <ColorField label="Nav Text" field="navTextHex" value={styles.navTextHex} onChange={handleChange} />
            <ColorField label="Nav Hover" field="navHoverHex" value={styles.navHoverHex} onChange={handleChange} />
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Footer</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <ColorField label="Footer Background" field="footerBgHex" value={styles.footerBgHex} onChange={handleChange} />
            <ColorField label="Footer Text" field="footerTextHex" value={styles.footerTextHex} onChange={handleChange} />
            <ColorField label="Footer Links" field="footerLinkHex" value={styles.footerLinkHex} onChange={handleChange} />
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Buttons & CTAs</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <ColorField label="Button Background" field="buttonBgHex" value={styles.buttonBgHex} onChange={handleChange} />
            <ColorField label="Button Text" field="buttonTextHex" value={styles.buttonTextHex} onChange={handleChange} />
          </div>
        </div>

        {/* Live colour preview */}
        <div className="pt-4 border-t">
          <p className="text-xs text-muted-foreground mb-3">Preview</p>
          <div className="flex flex-wrap gap-3 items-center">
            <div className="rounded px-4 py-2 text-sm font-medium" style={{ backgroundColor: styles.buttonBgHex, color: styles.buttonTextHex }}>Primary Button</div>
            <div className="rounded px-4 py-2 text-sm font-medium" style={{ backgroundColor: styles.accentHex, color: "#fff" }}>Accent</div>
            <div className="rounded px-4 py-2 text-sm font-medium border" style={{ backgroundColor: styles.backgroundHex, color: styles.textHex, borderColor: styles.borderHex }}>Background</div>
            <span className="text-sm font-medium" style={{ color: styles.linkHex }}>Link colour</span>
          </div>
        </div>

        {/* Two-colour heading preview */}
        <div className="pt-4 border-t">
          <p className="text-xs text-muted-foreground mb-2">
            Two-Colour Heading — pick these up as quick swatches in any heading's rich text colour picker
          </p>
          <p
            className="text-2xl font-semibold leading-snug"
            style={{ fontFamily: styles.headingFont || "inherit" }}
          >
            <span style={{ color: styles.headingHex }}>Sell your property</span>{" "}
            <span style={{ color: styles.headingAccentHex }}>without an estate agent</span>
          </p>
        </div>
      </section>

      {/* Typography */}
      <section className="border rounded-lg p-6 space-y-5">
        <div className="flex items-center gap-2 border-b pb-3">
          <Type className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Typography</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <FontField label="Heading Font" field="headingFont" value={styles.headingFont} onChange={handleChange} />
          <FontField label="Normal Font" field="bodyFont" value={styles.bodyFont} onChange={handleChange} />
          <FontField label="Navigation Font" field="navFont" value={styles.navFont} onChange={handleChange} />
          <FontField label="Bulleted / Numbered List Font" field="listFont" value={styles.listFont} onChange={handleChange} />
        </div>
        <p className="text-xs text-muted-foreground -mt-2">
          Normal Font is the site-wide default for regular (non-heading) text —
          every rich text editor's "Normal" paragraph style uses it unless an
          admin picks a different font for that specific text. List Font
          governs bulleted/numbered list items — leave blank to just use
          Normal Font for lists too.
        </p>

        <div className="grid grid-cols-2 gap-4 pt-2 border-t">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Base Font Size: {styles.baseFontSize || 16}px</Label>
            <input type="range" min="12" max="22" value={styles.baseFontSize || 16}
              onChange={(e) => handleChange("baseFontSize", parseInt(e.target.value))} className="w-full" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">List Font Size: {styles.listFontSize || 16}px</Label>
            <input type="range" min="12" max="22" value={styles.listFontSize || 16}
              onChange={(e) => handleChange("listFontSize", parseInt(e.target.value))} className="w-full" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Body Weight: {styles.bodyWeight || 400}</Label>
            <input type="range" min="300" max="700" step="100" value={styles.bodyWeight || 400}
              onChange={(e) => handleChange("bodyWeight", parseInt(e.target.value))} className="w-full" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Body Line Height: {styles.bodyLineHeight || 1.6}</Label>
            <input type="range" min="1.0" max="2.2" step="0.1" value={styles.bodyLineHeight || 1.6}
              onChange={(e) => handleChange("bodyLineHeight", parseFloat(e.target.value))} className="w-full" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Heading Weight: {styles.headingWeight || 700}</Label>
            <input type="range" min="300" max="900" step="100" value={styles.headingWeight || 700}
              onChange={(e) => handleChange("headingWeight", parseInt(e.target.value))} className="w-full" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">H1 Size: {styles.h1Size || 48}px</Label>
            <input type="range" min="24" max="96" value={styles.h1Size || 48}
              onChange={(e) => handleChange("h1Size", parseInt(e.target.value))} className="w-full" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">H2 Size: {styles.h2Size || 36}px</Label>
            <input type="range" min="20" max="64" value={styles.h2Size || 36}
              onChange={(e) => handleChange("h2Size", parseInt(e.target.value))} className="w-full" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">H3 Size: {styles.h3Size || 24}px</Label>
            <input type="range" min="16" max="48" value={styles.h3Size || 24}
              onChange={(e) => handleChange("h3Size", parseInt(e.target.value))} className="w-full" />
          </div>
          <div>
            <Label className="text-xs font-medium text-muted-foreground">H4 Size: {styles.h4Size || 20}px</Label>
            <input type="range" min="14" max="40" value={styles.h4Size || 20}
              onChange={(e) => handleChange("h4Size", parseInt(e.target.value))} className="w-full" />
          </div>
          <div>
            <Label className="text-xs font-medium text-muted-foreground">H5 Size: {styles.h5Size || 18}px</Label>
            <input type="range" min="12" max="32" value={styles.h5Size || 18}
              onChange={(e) => handleChange("h5Size", parseInt(e.target.value))} className="w-full" />
          </div>
          <div>
            <Label className="text-xs font-medium text-muted-foreground">H6 Size: {styles.h6Size || 16}px</Label>
            <input type="range" min="10" max="28" value={styles.h6Size || 16}
              onChange={(e) => handleChange("h6Size", parseInt(e.target.value))} className="w-full" />
          </div>
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Checkout Heading Size: {styles.checkoutHeadingSize || 30}px</Label>
            <input type="range" min="16" max="72" value={styles.checkoutHeadingSize || 30}
              onChange={(e) => handleChange("checkoutHeadingSize", parseInt(e.target.value))} className="w-full" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Heading Line Height: {styles.headingLineHeight || 1.2}</Label>
            <input type="range" min="0.9" max="1.8" step="0.1" value={styles.headingLineHeight || 1.2}
              onChange={(e) => handleChange("headingLineHeight", parseFloat(e.target.value))} className="w-full" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Space Below Headings: {styles.headingSpacing ?? 16}px</Label>
            <input type="range" min="0" max="60" step="2" value={styles.headingSpacing ?? 16}
              onChange={(e) => handleChange("headingSpacing", parseInt(e.target.value))} className="w-full"
              data-testid="site-styles-heading-spacing-slider" />
            <p className="text-xs text-muted-foreground -mt-1">Gap under every H1–H6 before the next element, site-wide.</p>
          </div>
          <div className="flex flex-col gap-1.5 col-span-2">
            <Label className="text-xs font-semibold text-foreground">Page Content Width</Label>
            <p className="text-xs text-muted-foreground -mt-1 mb-1">
              How wide each page's content area is, as a % of the screen, per device.
              Header/navigation/footer always stay full-width.
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Desktop Width: {styles.pageWidthDesktop ?? 90}%</Label>
            <input type="range" min="50" max="100" step="1" value={styles.pageWidthDesktop ?? 90}
              onChange={(e) => handleChange("pageWidthDesktop", parseInt(e.target.value))} className="w-full"
              data-testid="site-styles-page-width-desktop" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Tablet Width: {styles.pageWidthTablet ?? 95}%</Label>
            <input type="range" min="50" max="100" step="1" value={styles.pageWidthTablet ?? 95}
              onChange={(e) => handleChange("pageWidthTablet", parseInt(e.target.value))} className="w-full"
              data-testid="site-styles-page-width-tablet" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Mobile Width: {styles.pageWidthMobile ?? 100}%</Label>
            <input type="range" min="50" max="100" step="1" value={styles.pageWidthMobile ?? 100}
              onChange={(e) => handleChange("pageWidthMobile", parseInt(e.target.value))} className="w-full"
              data-testid="site-styles-page-width-mobile" />
          </div>
          <div className="flex flex-col gap-1.5 col-span-2">
            <Label className="text-xs font-semibold text-foreground">Modal Width</Label>
            <p className="text-xs text-muted-foreground -mt-1 mb-1">
              Forces every dialog/modal across the site to this % of the viewport on desktop screens.
              Mobile and tablet modals stay full-width, unaffected by this setting.
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Desktop Width: {styles.modalWidthPercent ?? 50}%</Label>
            <input type="range" min="20" max="100" step="1" value={styles.modalWidthPercent ?? 50}
              onChange={(e) => handleChange("modalWidthPercent", parseInt(e.target.value))} className="w-full"
              data-testid="site-styles-modal-width-desktop" />
          </div>
        </div>

        {/* Typography preview */}
        <div className="pt-4 border-t rounded-lg p-4" style={{ backgroundColor: styles.backgroundHex }}>
          <h4 className="mb-2 font-bold" style={{ fontFamily: styles.headingFont || "inherit", fontSize: `${Math.min(styles.headingFontSize || 32, 28)}px`, color: styles.headingHex }}>
            Heading Preview
          </h4>
          <p className="text-sm leading-relaxed" style={{ fontFamily: styles.bodyFont || "inherit", fontSize: `${styles.baseFontSize || 16}px`, color: styles.textHex }}>
            Body text preview — discover exceptional properties in prime locations. Your perfect home is waiting.
          </p>
          <a href="#" className="text-sm mt-2 inline-block" style={{ fontFamily: styles.navFont || styles.bodyFont || "inherit", color: styles.linkHex }}>
            Navigation link example
          </a>
        </div>
      </section>

      {/* Images */}
      <section className="border rounded-lg p-6 space-y-5">
        <div className="flex items-center gap-2 border-b pb-3">
          <ImageIcon className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Images</h3>
        </div>
        <p className="text-xs text-muted-foreground -mt-2">
          Applies to property photos, galleries, hero and module images across the public site. Logos, avatars and icons are unaffected.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">
              Corner Radius: {styles.imageBorderRadius || 0}px
            </Label>
            <input
              type="range"
              min="0"
              max="32"
              value={styles.imageBorderRadius || 0}
              onChange={(e) => handleChange("imageBorderRadius", parseInt(e.target.value))}
              className="w-full"
              data-testid="image-border-radius-slider"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">
              Shadow Intensity: {SHADOW_LABELS[styles.imageShadowIntensity || 0]}
            </Label>
            <input
              type="range"
              min="0"
              max="3"
              step="1"
              value={styles.imageShadowIntensity || 0}
              onChange={(e) => handleChange("imageShadowIntensity", parseInt(e.target.value))}
              className="w-full"
              data-testid="image-shadow-intensity-slider"
            />
          </div>
        </div>

        {/* Live preview */}
        <div className="pt-4 border-t">
          <p className="text-xs text-muted-foreground mb-3">Preview</p>
          <div
            className="h-28 w-40 bg-cover bg-center"
            style={{
              backgroundImage: "url(https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=320&q=60)",
              borderRadius: `${styles.imageBorderRadius || 0}px`,
              boxShadow: SHADOW_PREVIEW[styles.imageShadowIntensity || 0],
            }}
            data-testid="image-style-preview"
          />
        </div>
      </section>

      {/* Live preview wall: shows every module instance with the current styles */}
      <ModuleInstancesPreview />
    </div>
  );
}

export default SiteStylesAdminTab;
